
import React, { useEffect } from 'react';
import { formatText, parseTableRows } from '@/utils/report';
import { componentStyles } from '@/styles/theme';

type ReportTableProps = {
  content: string;
  isPositioningMap?: boolean;
};

// Define the ReportTable type in global namespace
declare global {
  interface Window {
    reportTables?: Array<{
      title?: string;
      content: string;
      data: Array<{
        cells: string[];
        isHeader: boolean;
        isDelimiter: boolean;
      }>;
      tableData?: string[][];
      isPositioningMap?: boolean;
    }>;
  }
}

const ReportTable = ({ content, isPositioningMap = false }: ReportTableProps) => {
  const rows = content.split('\n').filter(row => row.trim());
  const parsedRows = parseTableRows(rows);
  const { table } = componentStyles;
  
  // Extract table title if present (first row doesn't contain pipe character)
  let tableTitle = '';
  let tableRows = [...rows];
  
  if (rows.length > 0 && !rows[0].includes('|')) {
    tableTitle = rows[0];
    tableRows = rows.slice(1);
  }
  
  // Check if this appears to be a market positioning table
  const isMarketPositioningTable = 
    isPositioningMap || 
    tableTitle.toLowerCase().includes('positioning') ||
    tableTitle.toLowerCase().includes('competitive') ||
    tableRows[0]?.toLowerCase().includes('company') || 
    tableRows[0]?.toLowerCase().includes('competitor') ||
    tableRows[0]?.toLowerCase().includes('product');
  
  // Export the table content to the window object for PDF generation
  useEffect(() => {
    if (typeof window !== 'undefined' && parsedRows.length > 0) {
      if (!window.reportTables) window.reportTables = [];
      
      // Convert parsed rows to a 2D array format for easy consumption by PDF generator
      const headerRow = parsedRows.find(row => row.isHeader && !row.isDelimiter);
      
      // Filter out placeholder rows with dashes or empty content
      const bodyRows = parsedRows.filter(row => {
        // Skip delimiter rows
        if (row.isHeader || row.isDelimiter) return false;
        
        // Skip rows that contain only dashes, hyphens or placeholder text
        const isPlaceholderRow = row.cells.every(cell => {
          const trimmedCell = cell.trim();
          // Check for common placeholder patterns
          return trimmedCell === '' || 
                 /^[-–—]+$/.test(trimmedCell) || 
                 trimmedCell === '-------' ||
                 trimmedCell === '----------' ||
                 trimmedCell.includes('------');
        });
        
        return !isPlaceholderRow;
      });
      
      if (headerRow) {
        const tableData = [
          // Header row
          headerRow.cells.map(cell => formatText(cell.trim())),
          // Body rows - already filtered above
          ...bodyRows.map(row => 
            row.cells.map(cell => {
              // Handle placeholders
              if (cell.includes('XYZ') || cell.includes('ABC')) {
                return 'N/A';
              }
              return formatText(cell.trim());
            })
          )
        ];
        
        // Clean table data - remove any rows that are just dashes or empty
        const cleanedTableData = tableData.filter(row => 
          row.some(cell => {
            const trimmedCell = cell.trim();
            return trimmedCell.length > 0 && !(/^[-–—]+$/.test(trimmedCell));
          })
        );
        
        if (cleanedTableData.length > 1) { // At least header + one data row
          window.reportTables.push({
            title: tableTitle,
            content: content,
            data: parsedRows,
            tableData: cleanedTableData,
            isPositioningMap: isMarketPositioningTable
          });
        }
      }
    }
    
    // Cleanup function for when component unmounts
    return () => {
      if (typeof window !== 'undefined' && window.reportTables) {
        // Remove this table from the collection when component unmounts
        window.reportTables = window.reportTables.filter(t => t.content !== content);
      }
    };
  }, [content, parsedRows, tableTitle, isMarketPositioningTable]);
  
  // Separate header rows from body rows for rendering
  const headerRows = parsedRows.filter((row, index) => index === 0 && !row.isDelimiter);
  
  // Filter out placeholder rows for display
  const bodyRows = parsedRows.filter((row, index) => {
    // Skip header and delimiter rows
    if (index === 0 || row.isDelimiter) return false;
    
    // Skip rows that contain only dashes or placeholder text
    const isPlaceholderRow = row.cells.every(cell => {
      const trimmedCell = cell.trim();
      // Check for common placeholder patterns
      return trimmedCell === '' || 
             /^[-–—]+$/.test(trimmedCell) || 
             trimmedCell === '-------' ||
             trimmedCell === '----------' ||
             trimmedCell.includes('------');
    });
    
    return !isPlaceholderRow;
  });

  // Check if the table title is the same as the header row content
  // This is to avoid showing duplicate headers as highlighted in the user's image
  const isTitleDuplicateOfHeader = () => {
    if (!tableTitle || headerRows.length === 0) return false;
    
    // Get the first header row and join its cells
    const headerContent = headerRows[0].cells.join(' ').toLowerCase().trim();
    
    // Check if the title contains the header content or vice versa
    return tableTitle.toLowerCase().trim() === headerContent || 
           headerContent.includes(tableTitle.toLowerCase().trim()) ||
           tableTitle.toLowerCase().trim().includes(headerContent);
  };
  
  return (
    <div className={table.container}>
      {/* Only show the title if it's not a duplicate of the header */}
      {tableTitle && !isTitleDuplicateOfHeader() && (
        <h3 className="text-base font-semibold text-gray-300 mb-2">{tableTitle}</h3>
      )}
      <table className={table.base}>
        <thead>
          {headerRows.map((row, rowIndex) => {
            const cells = row.cells.map(cell => {
              if (cell.includes('XYZ') || cell.includes('ABC')) {
                return '<em>N/A</em>';
              }
              return cell;
            });
            
            return (
              <tr key={`header-${rowIndex}`} className={table.headerRow}>
                {cells.map((cellContent, cellIndex) => (
                  <th
                    key={cellIndex}
                    className={`${table.headerCell} text-center`}
                    dangerouslySetInnerHTML={{
                      __html: formatText(cellContent.trim())
                    }}
                  />
                ))}
              </tr>
            );
          })}
        </thead>
        <tbody>
          {bodyRows.map((row, rowIndex) => {
            const cells = row.cells.map(cell => {
              if (cell.includes('XYZ') || cell.includes('ABC')) {
                return '<em>N/A</em>';
              }
              return cell;
            });
            
            return (
              <tr
                key={`body-${rowIndex}`}
                className={isMarketPositioningTable 
                  ? `${table.bodyRow} even:${table.bodyRowAlt}`
                  : table.bodyRow
                }
              >
                {cells.map((cellContent, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={table.bodyCell}
                    dangerouslySetInnerHTML={{
                      __html: formatText(cellContent.trim())
                    }}
                  />
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ReportTable;
